import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import translations, { DEFAULT_LANGUAGE, AVAILABLE_LANGUAGES } from '../locales/translations';
import termVariants from '../locales/terminologyVariants';

const STORAGE_KEY = 'app_language';

const LanguageContext = createContext(null);

/**
 * Resolve a dotted key like "sidebar.fragen" to a string from the given dict.
 * Returns null if any segment is missing so callers can fall back.
 */
const resolveKey = (dict, key) => {
  if (!dict || typeof key !== 'string') return null;
  const parts = key.split('.');
  let node = dict;
  for (const part of parts) {
    if (node && typeof node === 'object' && part in node) {
      node = node[part];
    } else {
      return null;
    }
  }
  return typeof node === 'string' ? node : null;
};

/**
 * Replace {placeholders} in a string with values from the `vars` object.
 * Missing vars are left as-is so issues are visible during QA.
 */
const interpolate = (text, vars) => {
  if (!vars || typeof text !== 'string') return text;
  return text.replace(/\{(\w+)\}/g, (match, name) =>
    Object.prototype.hasOwnProperty.call(vars, name) ? String(vars[name]) : match
  );
};

const TERM_TOKENS = {
  termAgent: 'agent_singular',
  termAgents: 'agent_plural',
  termAgentAdmin: 'agent_admin_area',
  termInstruction: 'instruction_singular',
  termInstructions: 'instruction_plural',
  termRequest: 'request_singular',
  termRequests: 'request_plural',
};
const TERM_TOKEN_RE = /\{([\^_]?)(term[A-Za-z]+)\}/g;

// Returns null unless every term the variant uses is set for this organization.
const applyTerms = (text, terms) => {
  let complete = true;
  const result = text.replace(TERM_TOKEN_RE, (match, modifier, token) => {
    const value = terms[TERM_TOKENS[token]] || (token === 'termAgentAdmin' ? terms.agent_plural : undefined);
    if (!value) {
      complete = false;
      return match;
    }
    if (modifier === '^') return value.charAt(0).toUpperCase() + value.slice(1);
    if (modifier === '_') return value.charAt(0).toLowerCase() + value.slice(1);
    return value;
  });
  return complete ? result : null;
};

const selectTerms = (state) => state.terminology?.terms;

export const LanguageProvider = ({ children }) => {
  const terminology = useSelector(selectTerms);

  const [language, setLanguageState] = useState(() => {
    if (typeof window === 'undefined') return DEFAULT_LANGUAGE;
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored && translations[stored] ? stored : DEFAULT_LANGUAGE;
  });

  const setLanguage = useCallback((code) => {
    setLanguageState((prev) => {
      const next = translations[code] ? code : DEFAULT_LANGUAGE;
      if (typeof window !== 'undefined') {
        localStorage.setItem(STORAGE_KEY, next);
      }
      return next;
    });
  }, []);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = language;
    }
  }, [language]);

  /**
   * Translate a key with optional interpolation variables.
   * Fallback chain: active language -> default language -> key itself.
   */
  const t = useCallback(
    (key, vars) => {
      const langTerms = terminology?.[language];
      const variant = langTerms && termVariants[language]?.[key];
      // A list holds candidates from most to least specific; the first one whose terms are all set wins
      for (const candidate of variant ? [].concat(variant) : []) {
        const applied = applyTerms(candidate, langTerms);
        if (applied !== null) return interpolate(applied, vars);
      }
      const active = resolveKey(translations[language], key);
      if (active !== null) return interpolate(active, vars);
      const fallback = resolveKey(translations[DEFAULT_LANGUAGE], key);
      if (fallback !== null) return interpolate(fallback, vars);
      return key;
    },
    [language, terminology]
  );

  const value = useMemo(
    () => ({ language, setLanguage, t, availableLanguages: AVAILABLE_LANGUAGES }),
    [language, setLanguage, t]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

/**
 * Access translation helpers anywhere under <LanguageProvider>.
 * Safe to call outside the provider too — returns a no-op `t` that echoes keys
 * back, which keeps the UI from crashing during partial rollouts.
 */
export const useTranslation = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    return {
      language: DEFAULT_LANGUAGE,
      setLanguage: () => {},
      t: (key) => key,
      availableLanguages: AVAILABLE_LANGUAGES,
    };
  }
  return ctx;
};

export default LanguageContext;
